import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader, Pencil, PlugZap, Save, X } from 'lucide-react';
import EmptyState from '../governed-mcp-gateway/components/EmptyState';
import { StatusBadge } from '../governed-mcp-gateway/components/StatusBadges';
import { ghostBtn, HelpHint, panelClass, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import { apiTypeLabel, GOVERNANCE_HELP, LLM_GATEWAY_PATHS, providerLabel } from './llmGatewayConstants';
import { logLlmGateway } from './llmGatewayLogger';
import { useLlmGatewayPage } from './LlmGatewayScreen';
import { firstError, validateGovernance } from './llmGatewayForm';
import { useLlmGateway } from './llmGatewayStore';
import type { GovernanceSettings, LlmGateway } from './llmGatewayTypes';
import GovernancePoliciesEditor from './components/GovernancePoliciesEditor';

export default function GatewayDetailPage() {
  const { isLight, onToast } = useLlmGatewayPage();
  const { gatewayId } = useParams<{ gatewayId: string }>();
  const navigate = useNavigate();
  const { getGateway, getModel, updateGovernance, testConnection } = useLlmGateway();
  const gateway = gatewayId ? getGateway(gatewayId) : undefined;
  const model = gateway ? getModel(gateway.modelId) : undefined;
  const [testing, setTesting] = useState(false);
  const [editingGovernance, setEditingGovernance] = useState(false);
  const [governanceDraft, setGovernanceDraft] = useState<GovernanceSettings | null>(null);
  const [govErrors, setGovErrors] = useState<Record<string, string>>({});

  if (!gateway) {
    return (
      <EmptyState
        isLight={isLight}
        title="Gateway not found"
        description="This gateway is missing or was deleted."
        action={
          <button type="button" className={primaryBtn()} onClick={() => navigate(LLM_GATEWAY_PATHS.list)}>
            Back to list
          </button>
        }
      />
    );
  }

  const onTest = async () => {
    setTesting(true);
    logLlmGateway('app', 'test_connection_start', { id: gateway.id });
    try {
      const result = await testConnection(gateway.id);
      if (result.ok) {
        onToast('success', result.message);
        logLlmGateway('app', 'test_connection_ok', { id: gateway.id });
      } else {
        onToast('error', result.message);
        logLlmGateway('warning', 'test_connection_blocked', { id: gateway.id, status: gateway.status });
      }
    } catch (error) {
      onToast('error', error instanceof Error ? error.message : 'Connection test failed.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-5">
      <GatewaySummary gateway={gateway} modelName={model?.name ?? gateway.modelId} isLight={isLight} onEdit={() => navigate(LLM_GATEWAY_PATHS.edit(gateway.id))} />
      <ConnectionPanel gateway={gateway} testing={testing} isLight={isLight} onTest={onTest} />
      <GovernancePanel
        gateway={gateway}
        draft={governanceDraft}
        editing={editingGovernance}
        errors={govErrors}
        isLight={isLight}
        onStart={() => {
          setGovernanceDraft({ ...gateway.governance });
          setGovErrors({});
          setEditingGovernance(true);
          logLlmGateway('app', 'governance_edit_open', { id: gateway.id });
        }}
        onCancel={() => {
          setEditingGovernance(false);
          setGovernanceDraft(null);
          setGovErrors({});
        }}
        onChange={(next) => {
          setGovernanceDraft(next);
          setGovErrors({});
        }}
        onSave={() => {
          if (!governanceDraft) return;
          const errors = validateGovernance(governanceDraft);
          if (Object.keys(errors).length) {
            setGovErrors(errors);
            onToast('error', firstError(errors) ?? 'Complete governance settings before saving.');
            return;
          }
          void (async () => {
            try {
              const saved = await updateGovernance(gateway.id, governanceDraft);
              if (!saved) {
                onToast('error', 'Could not update governance policies.');
                return;
              }
              setEditingGovernance(false);
              setGovernanceDraft(null);
              onToast('success', 'Governance policies updated.');
            } catch (error) {
              onToast('error', error instanceof Error ? error.message : 'Could not update governance policies.');
            }
          })();
        }}
      />
    </div>
  );
}

function GatewaySummary({
  gateway,
  modelName,
  isLight,
  onEdit,
}: {
  gateway: LlmGateway;
  modelName: string;
  isLight: boolean;
  onEdit: () => void;
}) {
  return (
    <section className={`${panelClass(isLight)} p-5`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Gateway Details</h2>
          <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Published alias and bound model.</p>
        </div>
        <button type="button" className={ghostBtn(isLight)} onClick={onEdit}>
          <Pencil size={13} />
          Edit
        </button>
      </div>
      <dl className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
        <SummaryItem label="Gateway Name" value={gateway.name} isLight={isLight} />
        <div>
          <dt className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Status</dt>
          <dd className="mt-1">
            <StatusBadge status={gateway.status} />
          </dd>
        </div>
        <SummaryItem label="Registered Model" value={modelName} isLight={isLight} />
        <SummaryItem label="Provider" value={providerLabel(gateway.provider)} isLight={isLight} />
        <SummaryItem label="Gateway API Type" value={gateway.apiTypes.map(apiTypeLabel).join(', ')} isLight={isLight} />
        <div>
          <dt className={`text-xs flex items-center gap-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            Public Model Alias
            <HelpHint text={GOVERNANCE_HELP.publicAlias} />
          </dt>
          <dd className={`mt-1 font-mono text-xs ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>{gateway.publicAlias}</dd>
        </div>
      </dl>
    </section>
  );
}

function SummaryItem({ label, value, isLight }: { label: string; value: string; isLight: boolean }) {
  return (
    <div>
      <dt className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{label}</dt>
      <dd className={`mt-1 font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{value}</dd>
    </div>
  );
}

function ConnectionPanel({
  gateway,
  testing,
  isLight,
  onTest,
}: {
  gateway: LlmGateway;
  testing: boolean;
  isLight: boolean;
  onTest: () => void;
}) {
  return (
    <section className={`${panelClass(isLight)} p-5`}>
      <h2 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Connection</h2>
      <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        Call URL and API keys are issued from AI Usage quota assignment. This page binds the registered model.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className={primaryBtn()} disabled={testing} onClick={() => void onTest()}>
          {testing ? <Loader size={13} className="animate-spin" /> : <PlugZap size={13} />}
          Test Connection
        </button>
      </div>
      {gateway.publicAlias ? (
        <p className={`mt-3 font-mono text-xs ${isLight ? 'text-neutral-600' : 'text-neutral-300'}`}>
          Model alias: {gateway.publicAlias}
        </p>
      ) : null}
    </section>
  );
}

function GovernancePanel({
  gateway,
  draft,
  editing,
  errors,
  isLight,
  onStart,
  onCancel,
  onChange,
  onSave,
}: {
  gateway: LlmGateway;
  draft: GovernanceSettings | null;
  editing: boolean;
  errors: Record<string, string>;
  isLight: boolean;
  onStart: () => void;
  onCancel: () => void;
  onChange: (next: GovernanceSettings) => void;
  onSave: () => void;
}) {
  return (
    <section className={`${panelClass(isLight)} p-5`}>
      <GovernancePoliciesEditor
        governance={draft ?? gateway.governance}
        errors={errors}
        isLight={isLight}
        readOnly={!editing}
        onChange={onChange}
        action={
          editing ? (
            <>
              <button type="button" className={ghostBtn(isLight)} onClick={onCancel}>
                <X size={13} />
                Cancel
              </button>
              <button type="button" className={primaryBtn()} onClick={onSave}>
                <Save size={13} />
                Save policies
              </button>
            </>
          ) : (
            <button type="button" className={ghostBtn(isLight)} onClick={onStart}>
              <Pencil size={13} />
              Update
            </button>
          )
        }
      />
    </section>
  );
}
